// ─── SEDRA HOME DESIGN TOKENS ─────────────────────────────────────────────────
// Implements BRAND.md (repo root) — the approved design system. Every value
// here traces to a BRAND.md token; do not add colors/sizes that BRAND.md
// doesn't define. Legacy export names are kept so existing imports keep
// working, but they now resolve to brand values.

// ── BRAND.md §2.1 — brand palette (sampled from the official brand sheets) ──
export const BRAND = {
  sage:       '#959981',
  cream:      '#EDE7D5',
  tan:        '#C8A771',
  forest:     '#33402B',
};

// ── BRAND.md §2.2 — derived functional tokens ──
export const TOKENS = {
  sageInk:     '#6F7260',  // sage for TEXT (raw sage fails AA on light bg)
  sageWash:    '#F0F1EC',
  forestSoft:  '#45543A',  // hover state of forest
  olive:      '#5F6B4A',  // primary button fill (BRAND.md §2.2 action token)
  oliveDeep:  '#4C5739',  // button hover
  oliveWash:  '#EDEFE5',  // secondary button bg
  pistachio:     '#9DB884',  // pistachio accent surface (dark text)
  pistachioDeep: '#506B3D',  // pistachio hover / CTA fill (cream text)
  pistachioWash: '#E7EDDC',  // pale pistachio bands / nav
  btnLabel:   '#F3F1E4',
  btnDisabled:'#C7C6BC',
  tanWash:     '#F6EEDF',
  ink:         '#1A1F1B',
  inkMuted:    '#5C6157',
  line:        '#D9D9CD',
  card:        '#FFFFFF',
};

// ── BRAND.md §2.3 — status colors (functional, unchanged) ──
export const STATUS = {
  info:      '#2563eb',
  success:   '#16a34a',
  danger:    '#dc2626',
  warn:      '#d97706',
  warnDark:  '#92400e',
  infoBg:    '#dbeafe',
  successBg: '#dcfce7',
  dangerBg:  '#fee2e2',
  warnBg:    '#fef3c7',
};

// ── Legacy COLORS map — same keys the app already imports, brand values ──
export const COLORS = {
  brand:        TOKENS.olive,      // primary ACTIONS (buttons)
  action:       TOKENS.olive,
  actionHover:  TOKENS.oliveDeep,
  brandDark:    BRAND.forest,      // headings, emphasis
  brandDarker:  BRAND.forest,      // product names, dark buttons
  brandDeep:    TOKENS.forestSoft, // top bars
  brandTint:    BRAND.sage,        // soft accents
  brandWash:    TOKENS.sageWash,   // brand-tinted backgrounds

  page:         BRAND.cream,
  card:         TOKENS.card,
  line:         TOKENS.line,
  border:       TOKENS.line,
  imageWell:    '#ECEAE4',
  textMuted:    TOKENS.inkMuted,

  ...STATUS,
};

// ── BRAND.md §3 — typography ──
// Nomixa self-hosted from src/assets/fonts/nomixa/ (received 2026-07-09); DM Sans
// is the fallback if a weight fails to load. Arabic: Almarai for
// headings/brand moments, Tajawal for body. Keep in sync with
// src/styles/tokens/typography.css.
export const FONT         = "'Nomixa', 'DM Sans', 'Tajawal', system-ui, sans-serif"; // body (legacy alias)
export const FONT_BODY    = FONT;
export const FONT_HEAD_EN = "'Nomixa', 'DM Sans', system-ui, sans-serif";
export const FONT_HEAD_AR = "'Almarai', 'Tajawal', sans-serif";
export const headingFont = (lang) => (lang === 'ar' ? FONT_HEAD_AR : FONT_HEAD_EN);

// rem scale, BRAND.md §3.2
export const TYPE = {
  display: { size: '2.441rem', weight: 700, lh: 1.15 },
  h1:      { size: '1.953rem', weight: 700, lh: 1.2  },
  h2:      { size: '1.563rem', weight: 700, lh: 1.25 },
  h3:      { size: '1.25rem',  weight: 600, lh: 1.3  },
  bodyLg:  { size: '1.125rem', weight: 400, lh: 1.55 },
  body:    { size: '1rem',     weight: 400, lh: 1.55 },
  bodySm:  { size: '0.875rem', weight: 400, lh: 1.5  },
  caption: { size: '0.75rem',  weight: 500, lh: 1.4  },
};

// ── BRAND.md §4 — spacing (4px base) ──
export const SPACE = [0, 4, 8, 12, 16, 24, 32, 48, 64];

// ── BRAND.md §5 — radius & elevation (forest-tinted shadows) ──
export const RADIUS = { sm: 8, md: 12, lg: 16, pill: 999 };
export const SHADOW = {
  1: '0 1px 3px rgba(0,42,23,.08)',
  2: '0 4px 12px rgba(0,42,23,.10)',
  3: '0 12px 32px rgba(0,42,23,.16)',
};

// ── BRAND.md §6 — motion ──
export const MOTION = {
  micro: '150ms cubic-bezier(.2,.8,.4,1)',
  panel: '250ms cubic-bezier(.2,.8,.4,1)',
};

// ── CSS custom properties now live in src/styles/tokens/*.css (imported
// once from src/index.js) — that's the single source of truth for `var(--*)`
// CSS. These JS constants stay in sync with those files for components that
// need computed inline styles instead of CSS vars. ──

// ── Shared style fragments (BRAND.md §8) ──
export const input = {
  width: '100%', padding: '10px 13px', borderRadius: RADIUS.sm,
  border: `1px solid ${TOKENS.line}`, fontSize: 14,
  boxSizing: 'border-box', outline: 'none', background: TOKENS.card,
  fontFamily: FONT_BODY, color: TOKENS.ink,
};

// Defaults to the olive action color via COLORS.brand
export const button = (bg = COLORS.brand) => ({
  padding: '10px 20px', borderRadius: RADIUS.sm, background: bg, color: '#fff',
  border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  fontFamily: FONT_BODY, minHeight: 44,
});

export const cardBox = {
  background: TOKENS.card, borderRadius: RADIUS.md, padding: 20,
  boxShadow: SHADOW[1],
};

// Vendor status chip colors
export const VENDOR_STATUS = {
  pending:   { label_en: 'Pending',   label_ar: 'قيد المراجعة', color: STATUS.warn,     bg: STATUS.warnBg },
  active:    { label_en: 'Active',    label_ar: 'نشط',          color: STATUS.success,  bg: STATUS.successBg },
  suspended: { label_en: 'Suspended', label_ar: 'موقوف',        color: STATUS.danger,   bg: STATUS.dangerBg },
  rejected:  { label_en: 'Rejected',  label_ar: 'مرفوض',        color: TOKENS.inkMuted, bg: TOKENS.line },
};

// Product approval chip colors (vendor products)
export const APPROVAL_STATUS = {
  pending:  { label_en: 'Awaiting review', label_ar: 'بانتظار المراجعة', color: STATUS.warn,    bg: STATUS.warnBg },
  approved: { label_en: 'Live',            label_ar: 'منشور',            color: STATUS.success, bg: STATUS.successBg },
  rejected: { label_en: 'Rejected',        label_ar: 'مرفوض',            color: STATUS.danger,  bg: STATUS.dangerBg },
};
